import { PageProps } from "@/types/page";
import { usePage } from "@inertiajs/react";
import { useMediaQuery } from "@react-hook/media-query";
import classNames from "classnames";
import {
    addWeeks,
    eachDayOfInterval,
    endOfWeek,
    format,
    isAfter,
    isBefore,
    startOfWeek,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import {
    AvailableTimesByDay,
    dayKey,
    dayOfWeekInTimezone,
    eachDayOfIntervalInTimezone,
    endOfMonthInTimezone,
    MAX_WEEKS_AHEAD_TO_SCHEDULE,
    nextSaturdayInTimezone,
    previousSundayInTimezone,
    startOfMonthInTimezone,
} from "./dateTimePickerUtils";

type CalendarViewProps = {
    selectedDay: Date | null;
    month: Date;
    availableTimesByDay: AvailableTimesByDay;
    onMonthChange: (direction: "prev" | "next") => void;
    onDaySelect: (day: Date) => void;
    viewOnly?: boolean;
};

export default function CalendarView({
    selectedDay,
    month,
    availableTimesByDay,
    onMonthChange,
    onDaySelect,
    viewOnly = false,
}: CalendarViewProps) {
    const { user } = usePage<PageProps>().props;
    const userTimezone = user?.timezone ?? "UTC";
    const isSmall = useMediaQuery("(max-width: 480px)");

    const monthStart = startOfMonthInTimezone(month, userTimezone);
    const monthEnd = endOfMonthInTimezone(month, userTimezone);
    const calendarStart =
        dayOfWeekInTimezone(monthStart, userTimezone) === 0
            ? monthStart
            : previousSundayInTimezone(monthStart, userTimezone);
    const calendarEnd =
        dayOfWeekInTimezone(monthEnd, userTimezone) === 6
            ? monthEnd
            : nextSaturdayInTimezone(monthEnd, userTimezone);
    const days = eachDayOfIntervalInTimezone(
        calendarStart,
        calendarEnd,
        userTimezone,
    );

    const canGoBackAMonth = isAfter(monthStart, new Date());
    const canGoForwardAMonth = isBefore(
        monthEnd,
        addWeeks(new Date(), MAX_WEEKS_AHEAD_TO_SCHEDULE),
    );

    const referenceWeek = eachDayOfInterval({
        start: startOfWeek(new Date(0)),
        end: endOfWeek(new Date(0)),
    });

    return (
        <div className="flex-1 text-brand-black max-w-100">
            <div className="flex justify-between items-center mb-4">
                <div
                    data-testid="calendar-month-label"
                    className="text-xl font-bold"
                >
                    {formatInTimeZone(month, userTimezone, "MMMM yyyy")}
                </div>
                <div className="flex items-center gap-2">
                    {[
                        {
                            key: "prev",
                            disabled: !canGoBackAMonth,
                            onClick: () => onMonthChange("prev"),
                            className:
                                "border-brand-border border rounded-lg aspect-square hover:bg-brand-lightGray",
                            icon: (
                                <IoIosArrowBack className="text-xl text-brand-black min-w-8" />
                            ),
                        },
                        {
                            key: "next",
                            disabled: !canGoForwardAMonth,
                            onClick: () => onMonthChange("next"),
                            className:
                                "border-brand-border border rounded-lg aspect-square hover:bg-brand-lightGray",
                            icon: (
                                <IoIosArrowForward className="text-xl text-brand-black min-w-8" />
                            ),
                        },
                    ].map(({ key, disabled, onClick, icon }) => (
                        <button
                            key={key}
                            data-testid={`calendar-${key}-month-button`}
                            type="button"
                            onClick={onClick}
                            className={classNames(
                                "border-brand-border border rounded-lg aspect-square",
                                disabled
                                    ? "opacity-50 cursor-not-allowed"
                                    : "hover:bg-brand-lightGray",
                            )}
                            disabled={disabled}
                        >
                            {icon}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-7 gap-1 rounded-lg -mx-[13px]">
                <div className="grid grid-cols-subgrid col-span-7 rounded-lg">
                    {referenceWeek.map((day) => (
                        <div
                            key={day.toISOString()}
                            className="col-span-1 font-bold text-center xl:text-base text-sm h-6"
                        >
                            {format(day, isSmall ? "EEEEE" : "EEE")}
                        </div>
                    ))}
                </div>
                {days.map((day) => {
                    const key = dayKey(day, userTimezone);
                    const hasAvailability =
                        availableTimesByDay[key] &&
                        availableTimesByDay[key].length > 0;
                    const isToday =
                        dayKey(day, userTimezone) ===
                        dayKey(new Date(), userTimezone);
                    const isPastDay = day < new Date() && !isToday;
                    const isOutsideMonth = day < monthStart || day > monthEnd;
                    const isSelectedDay =
                        selectedDay &&
                        dayKey(day, userTimezone) ===
                            dayKey(selectedDay, userTimezone);
                    const canClickDay =
                        !isOutsideMonth &&
                        !isSelectedDay &&
                        (((user?.type === "client" || viewOnly) &&
                            hasAvailability) ||
                            (user?.type === "coach" &&
                                !viewOnly &&
                                !isPastDay));
                    return (
                        <button
                            key={key}
                            data-testid={
                                canClickDay
                                    ? "available-calendar-day"
                                    : undefined
                            }
                            className={classNames(
                                "flex flex-col items-center justify-center col-span-1 aspect-square rounded-lg",
                                isOutsideMonth
                                    ? "opacity-0 pointer-events-none"
                                    : "",
                                isSelectedDay
                                    ? "bg-brand-red text-brand-white"
                                    : "",
                                canClickDay
                                    ? "hover:bg-brand-lightGray"
                                    : "cursor-default",
                            )}
                            onClick={() => {
                                if (!canClickDay) return;
                                onDaySelect(day);
                            }}
                        >
                            <div
                                className={classNames(
                                    "text-base sm:text-lg",
                                    !hasAvailability ? "text-brand-border" : "",
                                )}
                            >
                                {formatInTimeZone(day, userTimezone, "d")}
                            </div>
                            {isToday && (
                                <div
                                    className={classNames(
                                        "h-0.5 w-5 rounded-full -mt-1.8 -mb-0.5",
                                        isSelectedDay
                                            ? "bg-brand-white"
                                            : !hasAvailability
                                              ? "bg-brand-border"
                                              : "bg-brand-gray",
                                    )}
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
